import { CreditCard } from "lucide-react";
import PortalModal from "../../Components/PortalModal";
import axios from "axios";
import { useEffect } from "react";
import { useState } from "react";

export const AddMoneyModal = ({
  showAddMoneyModal,
  setShowAddMoneyModal,
  addMoneyAmount,
  setAddMoneyAmount,
  renderWalletTitle,
  handleAddMoney,
  disabled,
}) => {
  const [walletData, setWalletData] = useState({
    balance: 0,
    currency: "₹",
    isLoading: true,
    lastUpdated: null,
    transactions: [],
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const MAX_BALANCE = 5000; // You can also get this from environment variables

  const fetchWalletBalance = async () => {
    try {
      setIsRefreshing(true);
      const userData = JSON.parse(localStorage.getItem("user"));

      if (!userData?.entityId) {
        console.error("No entityId found in user data");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wallet/${userData.entityId}`
      );

      if (response.data.success) {
        setWalletData({
          balance: response.data.balance,
          currency: response.data.currency === "INR" ? "₹" : "$",
          isLoading: false,
          lastUpdated: new Date(response.data.lastUpdated),
          transactions: response.data.transactions || [],
        });
      }
    } catch (err) {
      console.error("Error fetching wallet balance:", err);
      setWalletData((prev) => ({
        ...prev,
        isLoading: false,
      }));
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  const validateAmount = (amount) => {
    const amountNum = Number(amount);
    if (isNaN(amountNum)) {
      setError("Please enter a valid number");
      return false;
    }
    if (amountNum <= 0) {
      setError("Amount must be greater than 0");
      return false;
    }

    // Check if adding this amount would exceed the max balance
    const newBalance = walletData.balance + amountNum;
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData?.role !== "admin") {
      if (newBalance > MAX_BALANCE) {
        setError(`Cannot exceed maximum wallet balance of ${MAX_BALANCE}`);
        return false;
      }
    }

    setError(null);
    return true;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAddMoneyAmount(value);
    validateAmount(value);
  };

  const handlePayment = () => {
    if (!validateAmount(addMoneyAmount)) return;
    handleAddMoney();
  };
  if (!showAddMoneyModal) return null;
  return (
    <PortalModal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              Add Money to {renderWalletTitle()}
            </h3>
            <button
              onClick={() => {
                setShowAddMoneyModal(false);
                setError(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Amount (₹)
            </label>
            <input
              type="number"
              value={addMoneyAmount}
              onChange={handleAmountChange}
              placeholder="Enter amount"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              min="1"
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
            <p className="mt-1 text-sm text-gray-500">
              Current balance: {walletData.currency}
              {walletData.balance.toFixed(2)}
              <br />
              Maximum allowed balance: {walletData.currency}
              {MAX_BALANCE.toFixed(2)}
            </p>
          </div>

          <div className="text-xs text-yellow-600 mb-4 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Note: 3% convenience fee will be charged
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => {
                setShowAddMoneyModal(false);
                setError(null);
              }}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePayment}
              disabled={disabled || !!error || !addMoneyAmount}
              className={`flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center space-x-2 ${
                disabled || !!error || !addMoneyAmount
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <CreditCard className="h-4 w-4" />
              <span>Pay with Razorpay</span>
            </button>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
